import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/core/api";
import { followAiScriptExplanationChatTurn } from "@/payroll/ai-script/explanation-chat-transport";
import { invalidateReportViews } from "@/profitability/query";
import { reportError } from "@/profitability/utils";
import {
  templateChatKey,
  templateChatPath,
  useSendTemplateChat,
  useSummarizeTemplateChat,
  useTemplateChat,
} from "./template-chat-query";

/** Notes the conversation before them was summarized into; the replies read these instead. */
export const CONTEXT_SUMMARY = "context_summary";

export const useTemplateChatSession = (
  businessId: string,
  reportId: string,
  building: boolean,
  /** Read the conversation only where it can be seen or acted on. */
  enabled = true,
  /** The run on screen, sent with each message so the reply reads the figures shown. */
  resultRunId: string | null = null,
) => {
  const api = useApi();
  const client = useQueryClient();
  const chat = useTemplateChat(businessId, reportId, building, enabled);
  const send = useSendTemplateChat(businessId, reportId);
  const summarizer = useSummarizeTemplateChat(businessId, reportId);
  const wasBuilding = useRef(building);
  useEffect(() => {
    if (wasBuilding.current && !building)
      void client.invalidateQueries({
        queryKey: templateChatKey(businessId, reportId),
      });
    wasBuilding.current = building;
  }, [building, businessId, client, reportId]);
  const [liveText, setLiveText] = useState("");
  const [steps, setSteps] = useState<string[]>([]);
  const [followingTurn, setFollowingTurn] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const followed = useRef(new Set<string>());

  const follow = useCallback(
    (turnId: string) => {
      if (followed.current.has(turnId)) return;
      followed.current.add(turnId);
      setFollowingTurn(turnId);
      setLiveText("");
      setSteps([]);
      setError(null);
      void followAiScriptExplanationChatTurn({
        api,
        chatPath: templateChatPath(businessId, reportId),
        turnId,
        onTextDelta: (delta) => setLiveText((text) => text + delta),
        onProgress: (label) =>
          setSteps((current) =>
            current.at(-1) === label ? current : [...current, label],
          ),
      })
        .then((response) => {
          if (response.error) setError(response.message);
        })
        .catch((caught: unknown) => setError(reportError(caught)))
        .finally(() => {
          setFollowingTurn(null);
          void client.invalidateQueries({
            queryKey: templateChatKey(businessId, reportId),
          });
          // A turn may have rerun or removed a period: every view of the
          // agent's periods reads them again.
          void invalidateReportViews(client, businessId);
        });
    },
    [api, businessId, client, reportId],
  );

  const activeTurn = chat.data?.thread?.active_turn?.turn_id;
  useEffect(() => {
    if (activeTurn) follow(activeTurn);
  }, [activeTurn, follow]);

  const sendMessage = (
    message: string,
    documentIds: string[],
    images: File[],
  ) =>
    new Promise<boolean>((resolve) => {
      setError(null);
      send.mutate(
        {
          message,
          document_ids: documentIds,
          images,
          ...(resultRunId ? { result_run_id: resultRunId } : {}),
        },
        {
          onSuccess: ({ turn_id }) => {
            void client.invalidateQueries({
              queryKey: templateChatKey(businessId, reportId),
            });
            follow(turn_id);
            resolve(true);
          },
          onError: (caught) => {
            setError(reportError(caught));
            resolve(false);
          },
        },
      );
    });

  const summarize = () => {
    setError(null);
    summarizer.mutate(undefined, {
      onSuccess: ({ turn_id }) => follow(turn_id),
      onError: (caught) => setError(reportError(caught)),
    });
  };

  return {
    chat,
    messages: chat.data?.messages ?? [],
    /** Something said since the last notes, so there is something to summarize. */
    summarizable: !!chat.data?.thread?.summarizable,
    summarize,
    answering: !!followingTurn || send.isPending || summarizer.isPending,
    liveText,
    steps,
    error,
    sendMessage,
  };
};
